import { FormikProvider, useField, useFormik } from 'formik';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import {
	Button,
	Checkbox,
	Container,
	Editable,
	FormCard,
	Heading,
	Jumbo,
	Label,
	MultiSelect,
	NumericInput,
	Paragraph,
	Popover,
	QuillInput,
	Skeleton,
	Table,
	Tabs,
	Tags,
	Title,
	Toolbar,
} from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import GroupSelector from 'goals/group-selector/GroupSelector';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import { useLocalizedValue } from 'packages/kakapo-ui-application/hooks/useLocalizedState';
import moment from 'moment';

function Topics(props: { categories: graphql.EngagementCategory[] }) {
	const { localize } = useLocalize();

	const [{ value }, , { setValue }] = useField<graphql.EngagementCategory[]>('categories');
	const categories = value ?? props.categories;

	function option(category: graphql.EngagementCategory) {
		return {
			value: category.id,
			title: localize(category.title).text,
		};
	}

	return (
		<MultiSelect
			selectedOptions={categories.map(option)}
			options={props.categories.map(option)}
			onChange={(options) => setValue(props.categories.filter(({ id }) => options.includes(id)))}
		/>
	);
}

function IntervalSelector() {
	const { t } = useTranslation();
	const [{ value }, , { setValue }] = useField<graphql.EngagementInterval | undefined>('interval');

	return (
		<Tags.Item.Select
			label={t('settings.engagement.interval.every.label')}
			options={[
				{
					title: t('settings.engagement.interval.every.weekly'),
					value: graphql.EngagementInterval.Weekly,
				},
				{
					title: t('settings.engagement.interval.every.biweekly'),
					value: graphql.EngagementInterval.Biweekly,
				},
				{
					title: t('settings.engagement.interval.every.monthly'),
					value: graphql.EngagementInterval.Monthly,
				},
				{
					title: t('settings.engagement.interval.every.bimonthly'),
					value: graphql.EngagementInterval.Bimonthly,
				},
				{
					title: t('settings.engagement.interval.every.quarterly'),
					value: graphql.EngagementInterval.Quarterly,
				},
				{
					title: t('settings.engagement.interval.every.halfYearly'),
					value: graphql.EngagementInterval.HalfYearly,
				},
				{
					title: t('settings.engagement.interval.every.yearly'),
					value: graphql.EngagementInterval.Yearly,
				},
			]}
			selectedValue={value ?? graphql.EngagementInterval.Weekly}
			onChange={(value) => setValue(value as graphql.EngagementInterval)}
		/>
	);
}

function EngagementSettingsSummary() {
	const { t } = useTranslation();

	const [{ value }] = useField<graphql.EngagementInterval | undefined>('interval');

	if (!value || value === graphql.EngagementInterval.Weekly) {
		return <Paragraph>{t('settings.engagement.result.description.weekly')}</Paragraph>;
	} else if (value === graphql.EngagementInterval.Biweekly) {
		return <Paragraph>{t('settings.engagement.result.description.biweekly')}</Paragraph>;
	} else if (value === graphql.EngagementInterval.Monthly) {
		return <Paragraph>{t('settings.engagement.result.description.monthly')}</Paragraph>;
	} else if (value === graphql.EngagementInterval.Bimonthly) {
		return <Paragraph>{t('settings.engagement.result.description.bimonthly')}</Paragraph>;
	} else if (value === graphql.EngagementInterval.Quarterly) {
		return <Paragraph>{t('settings.engagement.result.description.quarterly')}</Paragraph>;
	} else if (value === graphql.EngagementInterval.HalfYearly) {
		return <Paragraph>{t('settings.engagement.result.description.halfYearly')}</Paragraph>;
	} else if (value === graphql.EngagementInterval.Yearly) {
		return <Paragraph>{t('settings.engagement.result.description.yearly')}</Paragraph>;
	} else {
		return null;
	}
}

function MaxQuestions(props: { defaults: graphql.EngagementIntervalDefault }) {
	const { t } = useTranslation();

	const [{ value: maxQuestions }, , { setValue: setMaxQuestions }] = useField<string | undefined>(
		'maxQuestions',
	);

	const [{ value: maxOpenQuestions }, , { setValue: setMaxOpenQuestions }] = useField<
		string | undefined
	>('maxOpenQuestions');

	return (
		<>
			<Flex flexGrow={1} flexShrink={1} flexBasis={0} ml={2} alignItems="center">
				<Label mr={2}>{t('settings.engagement.configuration.maxOpenQuestions.label')}:</Label>
				<Box maxWidth={40}>
					<NumericInput
						placeholder={props.defaults.maxOpenQuestions.toFixed()}
						value={maxOpenQuestions}
						onChange={setMaxOpenQuestions}
						small
					/>
				</Box>
			</Flex>
			<Flex flexGrow={1} flexShrink={1} flexBasis={0} ml={2} alignItems="center">
				<Label mr={2}>{t('settings.engagement.configuration.maxQuestions.label')}:</Label>
				<Box maxWidth={40}>
					<NumericInput
						placeholder={props.defaults.maxQuestions.toFixed()}
						value={maxQuestions}
						onChange={setMaxQuestions}
						small
					/>
				</Box>
			</Flex>
		</>
	);
}

function Expiry() {
	const { t } = useTranslation();

	const [{ value: expiry }, , { setValue: setExpiry }] = useField<string | undefined>('expiry');

	return (
		<Flex maxWidth={400} mt={3}>
			<Box flexGrow={1} flexShrink={1} flexBasis={0} mr={2}>
				<Label mb={2}>{t('settings.engagement.configuration.expiry.label')}:</Label>
				<NumericInput value={expiry || '7'} onChange={setExpiry} />
			</Box>
		</Flex>
	);
}

function EngagementSettingsStatusButton(props: {
	type?: 'primary' | 'secondary';
	title: string;
	loading: boolean;
	confirmTitle: string;
	onConfirm: () => void;
}) {
	const { t } = useTranslation();

	const [visible, setVisible] = useState(false);

	return (
		<Popover.Confirm
			visible={visible}
			title={props.confirmTitle}
			confirmTitle={t('settings.engagement.setup.confirm.yes')}
			cancelTitle={t('settings.engagement.setup.confirm.no')}
			extraInfo={t('settings.engagement.setup.confirm.timing')}
			onCancel={() => setVisible(false)}
			onConfirm={() => {
				props.onConfirm();
				setVisible(false);
			}}
		>
			<Button
				type={props.type}
				loading={props.loading}
				title={props.title}
				onClick={() => setVisible(true)}
			/>
		</Popover.Confirm>
	);
}

const EngagementSettingsFormToolbar = styled(Box)`
	position: fixed;
	bottom: 0;
	padding: 20px 0;
	background-color: ${(props) => props.theme.colors.white};
	width: 100%;
`;

const Header = () => {
	const [{ value }, , { setValue }] = useField<graphql.LocalizedString>('title');
	const [localizedValue, setLocalizedValue] = useLocalizedValue(value, setValue, '');

	if (localizedValue) {
		return (
			<Editable.Flex
				flexible
				value={localizedValue}
				onChange={(value) => {
					setLocalizedValue(value);
				}}
			>
				{(value) => <Title>{value || '&nbsp;'}</Title>}
			</Editable.Flex>
		);
	} else {
		return null;
	}
};

function EngagementSettingsForm(props: {
	template?: graphql.EngagementTemplate;
	defaults: graphql.EngagementIntervalDefault;
	userCount?: number;
	categories?: graphql.EngagementCategory[];
	toolbar?: ReactNode;
	requests?: graphql.EngagementRequest[];
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [disable, { loading: disableLoading }] = graphql.useDisableEngagementTemplateMutation();
	const [enable, { loading: enableLoading }] = graphql.useEnableEngagementTemplateMutation();
	const [send, { loading: sendLoading }] = graphql.useSendEngagementTemplateMutation();

	const title = props.template?.title ? localize(props.template?.title).text : '';

	const header = props.categories ? <Header /> : <Title.Skeleton />;

	return (
		<>
			<Jumbo
				title=" "
				alternativePageTitle={title}
				description={t('settings.engagement.description')}
				header={header}
				footer={<Tabs.Container />}
				button={
					<Flex>
						<Box mr={2}>
							<EngagementSettingsStatusButton
								type="secondary"
								loading={sendLoading}
								title={t('settings.engagement.setup.sendOnce')}
								confirmTitle={t('settings.engagement.setup.sendOnceConfirm', {
									count: props.userCount ?? 0,
								})}
								onConfirm={() => send({ variables: { input: { id: props.template!.id } } })}
							/>
						</Box>

						{props.categories && props.template ? (
							props.template.isEnabled ? (
								<EngagementSettingsStatusButton
									key="disable"
									loading={disableLoading}
									title={t('settings.engagement.setup.disable')}
									confirmTitle={t('settings.engagement.setup.disableConfirm', {
										count: props.userCount ?? 0,
									})}
									onConfirm={() => disable({ variables: { input: { id: props.template!.id } } })}
								/>
							) : (
								<EngagementSettingsStatusButton
									key="enable"
									loading={enableLoading}
									title={t('settings.engagement.setup.enable')}
									confirmTitle={t('settings.engagement.setup.enableConfirm', {
										count: props.userCount ?? 0,
									})}
									onConfirm={() => enable({ variables: { input: { id: props.template!.id } } })}
								/>
							)
						) : (
							<Button.Skeleton />
						)}
					</Flex>
				}
			/>
			<Container py={4}>
				<FormCard
					title={t('settings.engagement.setup.recipients.title')}
					description={t('settings.engagement.setup.recipients.description')}
				>
					{props.categories ? (
						<GroupSelector.Formik
							canSelect={() => true}
							types={[graphql.GroupType.Department]}
							name="targetGroupIDs"
						/>
					) : (
						<GroupSelector.Skeleton />
					)}
				</FormCard>
				<FormCard
					title={t('settings.engagement.topics.title')}
					description={t('settings.engagement.topics.description')}
				>
					{props.categories ? <Topics categories={props.categories} /> : <MultiSelect.Skeleton />}
				</FormCard>
				<FormCard
					title={t('settings.engagement.interval.title')}
					description={t('settings.engagement.interval.description')}
				>
					<Flex>
						{props.categories ? (
							<IntervalSelector />
						) : (
							<Tags.Item.Skeleton label={t('settings.engagement.interval.every.label')} />
						)}
						{props.template && <MaxQuestions defaults={props.defaults} />}
					</Flex>
					{props.template && <Expiry />}
				</FormCard>
				<FormCard
					title={t('settings.engagement.enps.title')}
					description={t('settings.engagement.enps.description')}
				>
					{props.categories ? (
						<Flex>
							<Checkbox.Formik name="useNps" />
							<Label ml={2}>{t('settings.engagement.enps.label')}</Label>
						</Flex>
					) : (
						<Checkbox.Skeleton />
					)}
				</FormCard>
				<FormCard
					title={t('settings.engagement.instruction.title')}
					description={t('settings.engagement.instruction.description')}
				>
					{props.categories ? (
						<QuillInput.Localizable.Formik name="instruction" />
					) : (
						<Skeleton.Box height={173} />
					)}
				</FormCard>
				<Box mb={5}>
					<Heading mb={2}>{t('settings.engagement.result.title')}</Heading>
					{props.categories ? (
						<EngagementSettingsSummary />
					) : (
						<Paragraph mb={2}>{t('settings.engagement.result.description.placeholder')} </Paragraph>
					)}
					{props.requests && (
						<>
							<Paragraph mb={2}>{t('settings.engagement.results.description')}</Paragraph>
							<Table>
								<Table.Header>
									<Table.Cell basis={1}>{t('settings.engagement.results.header.date')}</Table.Cell>
									<Table.Cell align="right" basis={0.2}>
										{t('settings.engagement.results.header.questions')}
									</Table.Cell>
									<Table.Cell align="right" basis={0.2}>
										{t('settings.engagement.results.header.sent')}
									</Table.Cell>
									<Table.Cell align="right" basis={0.2}>
										{t('settings.engagement.results.header.completed')}
									</Table.Cell>
									<Table.Cell align="right" basis={0.2}>
										{t('settings.engagement.results.header.percentage')}
									</Table.Cell>
								</Table.Header>
								{props.requests.map((request) => (
									<Table.Row key={request.id}>
										<Table.Cell basis={1}>
											{moment(request.createdAt).format('D MMMM YYYY HH:mm')}
										</Table.Cell>
										<Table.Cell align="right" basis={0.2}>
											{request.questions.length}
										</Table.Cell>
										<Table.Cell align="right" basis={0.2}>
											{request.sent}
										</Table.Cell>
										<Table.Cell align="right" basis={0.2}>
											{request.completed}
										</Table.Cell>
										<Table.Cell align="right" basis={0.2}>
											{Math.round(request.progress * 100)}%
										</Table.Cell>
									</Table.Row>
								))}
							</Table>
						</>
					)}
				</Box>
				{props.toolbar && (
					<EngagementSettingsFormToolbar mt={4}>{props.toolbar}</EngagementSettingsFormToolbar>
				)}
			</Container>
		</>
	);
}

function EngagementSettings(props: {
	template: graphql.EngagementTemplate;
	categories: graphql.EngagementCategory[];
	defaults: graphql.EngagementIntervalDefault[];
	requests: graphql.EngagementRequest[];
}) {
	const { t } = useTranslation();

	const [update, { loading }] = graphql.useUpdateEngagementTemplateMutation();

	const formik = useFormik({
		initialValues: {
			title: props.template.title,
			interval: props.template.interval,
			categories: props.template.categories,
			maxQuestions: props.template.maxQuestions?.toFixed(),
			maxOpenQuestions: props.template.maxOpenQuestions?.toFixed(),
			expiry: props.template.expiryInDays?.toFixed(),
			targetGroupIDs: props.template.targetGroups.map(({ id }) => id),
			useNps: props.template.useNps ?? false,
			instruction: props.template.instruction ?? [],
		},
		onSubmit: async (values) => {
			await update({
				variables: {
					input: {
						id: props.template.id,
						title: values.title,
						interval: values.interval,
						categoryIDs: values.categories?.map(({ id }) => id) ?? [],
						maxQuestions:
							values.maxQuestions === undefined ? undefined : parseInt(values.maxQuestions),
						maxOpenQuestions:
							values.maxOpenQuestions === undefined ? undefined : parseInt(values.maxOpenQuestions),
						expiryInDays: values.expiry === undefined ? undefined : parseInt(values.expiry),
						targetGroupIDs: values.targetGroupIDs,
						useNps: values.useNps,
						instruction: values.instruction,
					},
				},
			});
		},
	});

	const defaults = props.defaults.find((defaults) => defaults.interval === formik.values.interval);

	return (
		<FormikProvider value={formik}>
			<EngagementSettingsForm
				template={props.template}
				categories={props.categories}
				userCount={props.template.targetUsers.length}
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				defaults={defaults}
				requests={props.requests}
				toolbar={
					<Toolbar>
						<Button
							loading={loading}
							title={t('settings.engagement.toolbar.save')}
							onClick={() => formik.submitForm()}
						/>
					</Toolbar>
				}
			/>
		</FormikProvider>
	);
}

export default function EngagementSettingsWithoutData(props: { id: string }) {
	const query = graphql.useEngagementTemplateQuery({ variables: { id: props.id } });

	if (!query.data) {
		return (
			<EngagementSettingsForm
				defaults={{
					interval: graphql.EngagementInterval.Yearly,
					maxQuestions: 1,
					maxOpenQuestions: 1,
				}}
			/>
		);
	}

	const categories = query.data.builtinEngagementCategories.edges
		.concat(query.data.engagementCategories.edges)
		.map(({ node }) => node as graphql.EngagementCategory);

	const requests = query.data.engagementRequests.edges
		.map(({ node }) => node as graphql.EngagementRequest)
		.reverse();

	return (
		<EngagementSettings
			key={props.id}
			template={query.data.engagementTemplate as graphql.EngagementTemplate}
			categories={categories}
			defaults={query.data.engagementIntervalDefaults}
			requests={requests}
		/>
	);
}
